<template>
  <div class="kirchhoff-laws-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΣI_in = ΣI_out | ΣΔV = 0$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="260" viewBox="0 0 400 260">
          <defs>
             <filter id="glow-node"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-current" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                <path d="M0,0 L6,2 L0,4 Z" fill="#38bdf8" />
             </marker>
          </defs>

          <!-- Main Circuit Loop Layout -->
          <!-- Top Wire with Node -->
          <line x1="80" y1="60" x2="320" y2="60" stroke="#334155" stroke-width="2.5" />
          <circle cx="200" cy="60" r="6" fill="#f8fafc" filter="url(#glow-node)" />
          <text x="200" y="50" text-anchor="middle" fill="#f8fafc" font-size="10" font-weight="900">核心节点 (KCL)</text>

          <!-- Branch 1 (Left) -->
          <line x1="80" y1="60" x2="80" y2="200" stroke="#334155" stroke-width="2.5" />
          <rect x="65" y="100" width="30" height="40" fill="#1e293b" stroke="#f59e0b" stroke-width="2" rx="2" />
          <text x="60" y="125" text-anchor="end" fill="#f59e0b" font-size="10" font-weight="bold">R₁ = {{ r1 }}Ω</text>

          <!-- Branch 2 (Right) -->
          <line x1="320" y1="60" x2="320" y2="200" stroke="#334155" stroke-width="2.5" />
          <rect x="305" y="100" width="30" height="40" fill="#1e293b" stroke="#10b981" stroke-width="2" rx="2" />
          <text x="340" y="125" text-anchor="start" fill="#10b981" font-size="10" font-weight="bold">R₂ = {{ r2 }}Ω</text>

          <!-- Bottom Wire with Power Source -->
          <line x1="80" y1="200" x2="320" y2="200" stroke="#334155" stroke-width="2.5" />
          <g transform="translate(200, 200)">
             <line x1="-15" y1="-8" x2="15" y2="-8" stroke="#f8fafc" stroke-width="2.5" />
             <line x1="-8" y1="8" x2="8" y2="8" stroke="#f8fafc" stroke-width="4" />
             <text y="28" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="900">电源 V = {{ voltage }}V</text>
          </g>

          <!-- Animated Electron flow -->
          <g v-for="i in 12" :key="'e'+i">
             <!-- Path A (Left loop) -->
             <circle r="2.5" fill="#38bdf8" filter="url(#glow-node)">
                <animateMotion :dur="`${3/i1}s`" repeatCount="indefinite" 
                               path="M 200,200 L 80,200 L 80,60 L 200,60" />
             </circle>
             <!-- Path B (Right loop) -->
             <circle r="2.5" fill="#38bdf8" filter="url(#glow-node)">
                <animateMotion :dur="`${3/i2}s`" repeatCount="indefinite" 
                               path="M 200,200 L 320,200 L 320,60 L 200,60" />
             </circle>
          </g>

          <!-- Voltage Drop Labels (KVL Proof) -->
          <g font-size="9" font-weight="bold">
             <text x="110" y="110" fill="#f59e0b">ΔV₁ = -{{ (i1 * r1).toFixed(1) }}V</text>
             <text x="260" y="110" fill="#10b981">ΔV₂ = -{{ (i2 * r2).toFixed(1) }}V</text>
             <text x="200" y="150" fill="#f8fafc" text-anchor="middle" opacity="0.6">KVL: V + ΣΔV = 0</text>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>支路电流 I₁ (KCL)</label>
              <span class="val">{{ i1.toFixed(2) }} <small>A</small></span>
           </div>
           <div class="hud-item green">
              <label>支路电流 I₂ (KCL)</label>
              <span class="val">{{ i2.toFixed(2) }} <small>A</small></span>
           </div>
           <div class="hud-item orange">
              <label>电源总电流</label>
              <span class="val">{{ (i1 + i2).toFixed(2) }} <small>A</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>负载电阻 R₁ (Ω)</label>
                 <el-slider v-model="r1" :min="1" :max="50" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>负载电阻 R₂ (Ω)</label>
                 <el-slider v-model="r2" :min="1" :max="50" size="small"></el-slider>
              </div>
              <div class="s-item" style="grid-column: span 2;">
                 <label>供电电压 V (伏特)</label>
                 <el-slider v-model="voltage" :min="1" :max="36" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>电路分析之基：</strong>基尔霍夫定律是电磁学的双基石：[1] <strong>电流定律 (KCL)</strong> 意味着节点不积累电荷（ΣI=0），流入等于流出；[2] <strong>电压定律 (KVL)</strong> 意味着沿闭合路径走一圈回到原点，能量势能差之和为零（ΣU=0）。观察调节电阻时，各支路电流如何为了维持能量守恒而自动重新分配。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KirchhoffLawsViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      voltage: 12,
      r1: 10,
      r2: 15
    }
  },
  computed: {
    i1() { return this.voltage / this.r1; },
    i2() { return this.voltage / this.r2; }
  }
}
</script>

<style scoped lang="scss">
.kirchhoff-laws-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 6px 10px; min-width: 100px; label { display: block; font-size: 9px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #0ea5e9; } &.green { border-left: 3px solid #10b981; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #fdfcea; padding: 10px 14px; border-radius: 10px; border: 1px solid #fef3c7; display: flex; align-items: flex-start; gap: 10px; i { color: #d97706; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #92400e; line-height: 1.5; strong { color: #78350f; } } }
</style>
